import { useAuth } from "../../auth/AuthContext";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PanelLoading } from "../../components/Loading";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { formatDate, formatDateTime } from "../../lib/format";
import Ic from "../../components/icons.jsx";
import type { ApiError } from "../../api/client";
import { jwtKeyApi, type JwtKey, type JwtKeys } from "../../api/security";

/**
 * Ključevi kojima portal potpisuje prijave.
 *
 * Promena ključa se obavlja sama: zakazani posao svakog dana proveri koliko je aktivni ključ star i
 * zameni ga kad pređe propisani rok. Pitanje koje ekran odgovara jeste da li se to zaista dešava —
 * koliko je aktivni ključ star, koji je sledeći rok, i da zakazani posao nije ugašen podešavanjem.
 *
 * Promena ne prekida ni jednu prijavu: pristupni token važi petnaest minuta, a stari ključ se još
 * priznaje za vreme prelaznog roka, pa se niko ne odjavljuje zbog promene. Zato je dugme za promenu
 * ovde — ono je odgovor na sumnju da je ključ procurio, i ne čeka raspored.
 */
export default function AdminJwtKeysPage() {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<JwtKeys | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await jwtKeyApi.list());
    } catch (e) {
      toast.error((e as ApiError).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function rotate() {
    const ok = await confirm({
      title: t("jwtKeys.rotate.title", "Promena ključa"),
      message: t(
        "jwtKeys.rotate.ask",
        "Od sada se potpisuje novim ključem. Stari se još priznaje {{n}} dana, pa se niko ne odjavljuje. Promeniti?",
        { n: data?.rotation.graceWindowDays ?? 1 },
      ),
      confirmLabel: t("jwtKeys.rotate.confirm", "Promeni ključ"),
    });
    if (!ok) return;
    setBusy(true);
    try {
      setData(await jwtKeyApi.rotate());
      toast.success(t("jwtKeys.rotate.done", "Ključ je promenjen."));
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const active = data?.keys.find((k) => k.status === "ACTIVE") ?? null;
  const ageDays = active ? daysSince(active.validFrom) : null;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("jwtKeys.title", "Ključevi za potpisivanje prijava")}</h1>
          <div className="sub">
            {t(
              "jwtKeys.lead",
              "Portal potpisuje prijave jednim ključem i menja ga sam. Ovde se vidi koji je na redu, koliko je star i šta je ostalo od prethodnih.",
            )}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => void load()} disabled={busy}>
            <Ic.refresh /> {t("common.refresh", "Osveži")}
          </button>
          <button type="button" className="btn primary" disabled={busy || !data || !hasPermission("security:manage")} onClick={() => void rotate()}>
            {busy ? t("jwtKeys.rotating", "Menjam…") : t("jwtKeys.rotate.action", "Promeni ključ sada")}
          </button>
        </div>
      </div>

      {data === null ? (
        <PanelLoading text={t("jwtKeys.loading", "Čitam ključeve…")} />
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("jwtKeys.policy.scheduled", "automatska promena")}</div>
                <span className={`tag ${data.rotation.enabled ? "ok" : "err"}`}>
                  {data.rotation.enabled
                    ? t("jwtKeys.policy.on", "radi")
                    : t("jwtKeys.policy.off", "isključena")}
                </span>
              </div>
            </div>
            <Figure
              label={t("jwtKeys.policy.interval", "rok ključa")}
              value={t("jwtKeys.days", "{{n}} dana", { n: data.rotation.intervalDays })}
            />
            <Figure
              label={t("jwtKeys.policy.age", "aktivni ključ star")}
              value={ageDays === null ? "—" : t("jwtKeys.days", "{{n}} dana", { n: ageDays })}
            />
            <Figure
              label={t("jwtKeys.policy.due", "sledeća promena oko")}
              value={
                active === null
                  ? "—"
                  : formatDate(addDays(active.validFrom, data.rotation.intervalDays))
              }
            />
          </div>

          <p className="muted" style={{ margin: 0 }}>
            {data.rotation.enabled
              ? t(
                  "jwtKeys.policy.graceHint",
                  "Zamenjeni ključ se priznaje još {{n}} dana, pa promena nikoga ne odjavljuje.",
                  { n: data.rotation.graceWindowDays },
                )
              : t(
                  "jwtKeys.policy.offHint",
                  "Zakazani posao je ugašen podešavanjem, pa ključ stari dok ga neko ne promeni rukom.",
                )}
          </p>

          {data.keys.length === 0 ? (
            <div className="empty">{t("jwtKeys.none", "Nema ni jednog ključa.")}</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("jwtKeys.col.id", "Ključ")}</th>
                  <th>{t("jwtKeys.col.status", "Stanje")}</th>
                  <th>{t("jwtKeys.col.validFrom", "Potpisuje od")}</th>
                  <th>{t("jwtKeys.col.retiredAt", "Zamenjen")}</th>
                  <th>{t("jwtKeys.col.revokedAt", "Povučen")}</th>
                  <th>{t("jwtKeys.col.by", "Promenio")}</th>
                </tr>
              </thead>
              <tbody>
                {data.keys.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <code>#{k.id}</code>
                    </td>
                    <td>
                      <span className={`tag ${statusTone(k.status)}`}>{statusLabel(t, k.status)}</span>
                    </td>
                    <td>{formatDateTime(k.validFrom)}</td>
                    <td className="muted">{k.retiredAt ? formatDateTime(k.retiredAt) : "—"}</td>
                    <td className="muted">{k.revokedAt ? formatDateTime(k.revokedAt) : "—"}</td>
                    <td className="muted">
                      {k.createdByName ?? t("jwtKeys.bySchedule", "zakazana promena")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="card-b">
        <div className="muted">{label}</div>
        <strong style={{ fontSize: 22 }}>{value}</strong>
      </div>
    </div>
  );
}

function statusTone(status: JwtKey["status"]): string {
  if (status === "ACTIVE") return "ok";
  if (status === "RETIRED") return "neutral";
  return "err";
}

function statusLabel(t: TFunction, status: JwtKey["status"]): string {
  switch (status) {
    case "ACTIVE":
      return t("jwtKeys.status.ACTIVE", "potpisuje");
    case "RETIRED":
      return t("jwtKeys.status.RETIRED", "još se priznaje");
    case "REVOKED":
      return t("jwtKeys.status.REVOKED", "povučen");
  }
}

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function addDays(iso: string, days: number): string {
  return new Date(new Date(iso).getTime() + days * 86_400_000).toISOString();
}
